import React, {useEffect, useState} from 'react'
import AsyncSelect from 'react-select/async'
import PropTypes from 'prop-types'
import _ from 'lodash'
import debounce from 'debounce-promise'
import {fetchUsers} from '../../utils/services'
import PapiUser from './PapiUser'
import LoadingIndicator from '../shared/LoadingIndicator'
import DropdownIndicator from '../shared/DropdownIndicator'
import {CLASSNAME, CLASSNAME_PREFIX, componentClassName, isRequiredClassName} from '../constants'
import FloatingLabel from '../shared/FloatingLabel'
import useFocussedPlaceholder from '../../utils/useFocussedPlaceholder'
import {waitForCachedUser, waitForCachedUsers} from './userCache'

const fetchMatchingItems = (
    nonSelectableItems,
    searchCallback,
    customFields,
    tokenProvider,
    profileApiUrl
) =>
    debounce(
        (input) =>
            fetchUsers(input, nonSelectableItems, customFields, tokenProvider, profileApiUrl).then(
                (options) => Promise.resolve(searchCallback(options))
            ),
        300
    )

const renderItem = (user, size, showLink, tokenProvider, profileApiUrl) =>
    user.profilePhotoSrc || user.name === 'DOES-NOT-EXIST' ? (
        <PapiUser
            user={user}
            size={size}
            showLink={showLink}
            tokenProvider={tokenProvider}
            profileApiUrl={profileApiUrl}
        />
    ) : (
        <PapiUser
            id={user.id}
            size={size}
            showLink={showLink}
            tokenProvider={tokenProvider}
            profileApiUrl={profileApiUrl}
        />
    )

const renderValue =
    (size, showLink, tokenProvider, profileApiUrl) =>
    ({children, data}) =>
        children.props.user
            ? renderItem(children.props.user, size, showLink, tokenProvider, profileApiUrl)
            : renderItem({id: data.id}, size, showLink, tokenProvider, profileApiUrl)

const PapiUserSelect = (props) => {
    const {
        title,
        outlined,
        value,
        isRequired,
        nonSelectableItems = [],
        size,
        showLink = false,
        showOptionLink = false,
        searchCallback = (items) => items,
        customFields,
        tokenProvider,
        profileApiUrl
    } = props
    // console.log('VALUE', value)
    const [originalPlaceholder] = useState(props.placeholder)

    useEffect(() => {
        const hydrateValues = async () => {
            if (value) {
                if (Array.isArray(value)) {
                    const [full, needQuerying] = _.partition(value, (val) => val.profilePhotoSrc)
                    if (!_.isEmpty(needQuerying)) {
                        const loaded = await waitForCachedUsers(
                            needQuerying.map((val) => val.id),
                            true,
                            tokenProvider,
                            profileApiUrl
                        )
                        props.onChange(full.concat(loaded))
                    }
                } else if (!value.profilePhotoSrc) {
                    props.onChange(
                        await waitForCachedUser(value.id, true, tokenProvider, profileApiUrl)
                    )
                }
            }
        }
        hydrateValues().catch((err) => console.error('Error occurred hydrating user values: ', err))
    }, [JSON.stringify(value)])

    const {isFocussed, placeholder, handleOnFocus, handleOnBlur} = useFocussedPlaceholder(
        false,
        originalPlaceholder || title,
        isRequired
    )
    return (
        <div className={`papi-react ${isRequiredClassName({isRequired, value})}`}>
            <div className={componentClassName(outlined)}>
                <FloatingLabel
                    title={title}
                    value={value}
                    isFocussed={isFocussed}
                    isRequired={isRequired}
                    hasCustomPlaceholder={!!originalPlaceholder}
                />
                <AsyncSelect
                    id="group-select"
                    className={CLASSNAME}
                    classNamePrefix={CLASSNAME_PREFIX}
                    placeholder={placeholder}
                    getOptionLabel={(option) => renderItem(option, size, showOptionLink)}
                    getOptionValue={(option) => option.id}
                    loadOptions={fetchMatchingItems(
                        nonSelectableItems,
                        searchCallback,
                        customFields,
                        tokenProvider,
                        profileApiUrl
                    )}
                    openMenuOnFocus={false}
                    openMenuOnClick={false}
                    isClearable
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}
                    cacheOptions
                    closeMenuOnSelect
                    components={{
                        LoadingIndicator,
                        SingleValue: renderValue(size, showLink, tokenProvider, profileApiUrl),
                        MultiValueLabel: renderValue(size, showLink, tokenProvider, profileApiUrl),
                        DropdownIndicator
                    }}
                    {...props}
                />
            </div>
        </div>
    )
}

PapiUserSelect.displayName = 'PapiUserSelect'

PapiUserSelect.propTypes = {
    title: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    outlined: PropTypes.bool,
    isRequired: PropTypes.bool,
    isMulti: PropTypes.bool,
    showLink: PropTypes.bool,
    showOptionLink: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large', 'x-large']),
    searchCallback: PropTypes.func,
    customFields: PropTypes.string
}

export default PapiUserSelect
